import type { AppSettings, Routine, Session } from './types';

export function localDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

export function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

export function dayDifference(from: string, to: string): number {
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

export function cycleDayFrom(startDay: number, offset: number): number {
  return ((startDay - 1 + offset) % 7 + 7) % 7 + 1;
}

export function routineForDay(day: number, routines: Routine[]): Routine | undefined {
  return routines.find(routine => routine.dayOfCycle === day);
}

export function routineForDate(date: Date, settings: AppSettings | undefined, routines: Routine[], today = new Date()): Routine | undefined {
  if (!settings || routines.length === 0) return routines[0];
  const offset = dayDifference(localDate(today), localDate(date));
  if (offset < 0) return undefined;
  return routineForDay(cycleDayFrom(settings.nextRoutineDay, offset), routines);
}

export function historicalRoutineForDate(value: string, sessions: Session[], routines: Routine[]): Routine | undefined {
  const actual = sessions.find(session => session.localDate === value && session.finishedAt);
  if (actual) return routines.find(routine => routine.id === actual.routineId);
  const previous = sessions
    .filter(session => Boolean(session.finishedAt) && session.localDate < value)
    .sort((a, b) => b.localDate.localeCompare(a.localDate))[0];
  if (!previous) return undefined;
  const base = routines.find(routine => routine.id === previous.routineId);
  return base ? routineForDay(cycleDayFrom(base.dayOfCycle, dayDifference(previous.localDate, value)), routines) : undefined;
}

export function displayRoutineForDate(date: Date, settings: AppSettings | undefined, routines: Routine[], sessions: Session[], today = new Date()): Routine | undefined {
  const value = localDate(date);
  return value < localDate(today) ? historicalRoutineForDate(value, sessions, routines) : routineForDate(date, settings, routines, today);
}

export function sessionsForDate(value: string, sessions: Session[]): Session[] {
  return sessions.filter(session => session.localDate === value && Boolean(session.finishedAt));
}

export function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date);
}

export function startOfCalendarMonth(date: Date): Date {
  const first = new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0);
  const mondayOffset = (first.getDay() + 6) % 7;
  return addDays(first, -mondayOffset);
}
